import { InsurancePlan } from 'src/services/interfaces/insurance';

type Price = InsurancePlan['prices'][0];

export interface PriceSearchConditions {
  coverageAmount: number;
  primaryAge: number;
  dependentsCount: number;
  hasSpouse: boolean;
}

export const findValidCriticalIllnessPrice = (
  plan: InsurancePlan,
  conditions: PriceSearchConditions
): Price | undefined => {
  if (!plan.prices || plan.prices.length === 0) {
    return undefined;
  }

  const { coverageAmount, primaryAge, dependentsCount, hasSpouse } = conditions;
  let coverageType: number;
  if (hasSpouse && dependentsCount > 0) {
    coverageType = 4;
  } else if (hasSpouse) {
    coverageType = 2;
  } else if (dependentsCount > 0) {
    coverageType = 3;
  } else {
    coverageType = 1;
  }

  const exactMatch = plan.prices.find((price) => {
    const priceData = price.data;
    if (!priceData) return false;

    return (
      priceData.coverageAmount === coverageAmount &&
      priceData.dependentCount === dependentsCount &&
      priceData.minAge <= primaryAge &&
      priceData.maxAge >= primaryAge &&
      price.coverage === coverageType
    );
  });

  const validPrices = plan.prices.filter((price) => price.data);

  const closestMatch = validPrices
    .filter((price) => {
      const priceData = price.data!;

      return (
        priceData.coverageAmount === coverageAmount &&
        priceData.minAge <= primaryAge &&
        priceData.maxAge >= primaryAge &&
        price.coverage === coverageType
      );
    })
    .reduce((closest, current) => {
      if (!closest) return current;
      const closestDiff = Math.abs(
        closest.data!.dependentCount - dependentsCount
      );
      const currentDiff = Math.abs(
        current.data!.dependentCount - dependentsCount
      );
      return currentDiff < closestDiff ? current : closest;
    });

  if (closestMatch) {
    return closestMatch;
  }
  return undefined;
};

export const getCriticalIllnessPriceId = (
  plan: InsurancePlan,
  conditions: PriceSearchConditions
): number | undefined => {
  const validPrice = findValidCriticalIllnessPrice(plan, conditions);

  const result = validPrice?.id;
  return result;
};

export const findCriticalIllnessPriceId = (
  plan: InsurancePlan,
  coverageAmount: number,
  primaryAge: number,
  planTags: string[]
): number | undefined => {
  const hasSpouse = planTags.some((tag) => tag.includes('spouse'));
  const dependentsCount = planTags.filter((tag) =>
    tag.includes('dependent')
  ).length;

  const conditions: PriceSearchConditions = {
    coverageAmount,
    primaryAge,
    dependentsCount,
    hasSpouse,
  };

  return getCriticalIllnessPriceId(plan, conditions);
};

export const calculateCriticalIllnessPrice = (
  coverageAmount: number,
  primaryAge: number,
  planTags: string[]
): number => {
  const getRateForAge = (age: number): number => {
    if (age < 25) return 0.48;
    if (age >= 25 && age <= 29) return 0.52;
    if (age >= 30 && age <= 34) return 0.69;
    if (age >= 35 && age <= 39) return 1.04;
    if (age >= 40 && age <= 44) return 1.56;
    if (age >= 45 && age <= 49) return 2.3;
    if (age >= 50 && age <= 54) return 3.29;
    if (age >= 55 && age <= 59) return 4.25;
    if (age >= 60 && age <= 64) return 5.16;
    if (age >= 65 && age <= 69) return 6.46;
    if (age >= 70 && age <= 74) return 8.62;
    return 11.44;
  };

  const insuranceUnits = coverageAmount / 1000;

  const adultRate = getRateForAge(primaryAge);
  const childRate = 0.09;

  const adultPrice = insuranceUnits * adultRate;
  const childPrice = insuranceUnits * childRate;

  const hasSpouse = planTags.some((tag) => tag.includes('spouse'));
  const childCount = planTags.filter((tag) => tag.includes('dependent')).length;

  let totalPrice = adultPrice;

  if (hasSpouse) {
    totalPrice += adultPrice;
  }

  if (childCount > 0) {
    totalPrice += childPrice * childCount;
  }

  return totalPrice;
};
